import React, { useState, useEffect } from "react";
import { designationAPI, departmentAPI } from "../../lib/api";
import { toast } from "sonner";
import { formatDate } from "../../lib/utils";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { Plus, Briefcase, Loader2 } from "lucide-react";

const DesignationsPage = () => {
  const [designations, setDesignations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    level: 5,
    department_id: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [desigsRes, deptsRes] = await Promise.all([
        designationAPI.getAll(),
        departmentAPI.getAll(),
      ]);
      setDesignations(desigsRes.data);
      setDepartments(deptsRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreateLoading(true);
    try {
      const data = { ...formData };
      if (!data.department_id) delete data.department_id;
      await designationAPI.create(data);
      toast.success("Designation created successfully");
      setShowCreateDialog(false);
      setFormData({ name: "", level: 5, department_id: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create designation");
    } finally {
      setCreateLoading(false);
    }
  };

  const getDepartmentName = (deptId) => {
    const dept = departments.find((d) => d.id === deptId);
    return dept?.name || "All Departments";
  };

  const getLevelBadge = (level) => {
    const colors = {
      1: "bg-red-100 text-red-700",
      2: "bg-orange-100 text-orange-700",
      3: "bg-amber-100 text-amber-700",
      4: "bg-emerald-100 text-emerald-700",
      5: "bg-slate-100 text-slate-700",
    };
    return colors[level] || colors[5];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="designations-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Designations</h1>
          <p className="text-slate-500">Manage job titles and levels</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="create-designation-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Designation
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading">Create Designation</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div className="form-group">
                <Label>Designation Name</Label>
                <Input
                  placeholder="Software Engineer"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  data-testid="designation-name-input"
                />
              </div>
              <div className="form-group">
                <Label>Level (1 = Highest)</Label>
                <Select value={String(formData.level)} onValueChange={(value) => setFormData({ ...formData, level: parseInt(value) })}>
                  <SelectTrigger data-testid="designation-level-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">Level 1 (C-Suite)</SelectItem>
                    <SelectItem value="2">Level 2 (Director)</SelectItem>
                    <SelectItem value="3">Level 3 (Manager)</SelectItem>
                    <SelectItem value="4">Level 4 (Senior)</SelectItem>
                    <SelectItem value="5">Level 5 (Entry)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="form-group">
                <Label>Department (Optional)</Label>
                <Select value={formData.department_id} onValueChange={(value) => setFormData({ ...formData, department_id: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="All departments" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">All Departments</SelectItem>
                    {departments.map((dept) => (
                      <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full btn-primary" disabled={createLoading} data-testid="submit-create-designation">
                {createLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Designation"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Designations Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Level</TableHead>
                <TableHead>Department</TableHead>
                <TableHead>Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {designations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8 text-slate-500">
                    No designations found
                  </TableCell>
                </TableRow>
              ) : (
                designations.map((desig) => (
                  <TableRow key={desig.id} data-testid={`designation-row-${desig.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                          <Briefcase className="w-5 h-5 text-indigo-600" />
                        </div>
                        <span className="font-medium">{desig.name || desig.title}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={getLevelBadge(desig.level)}>
                        Level {desig.level}
                      </Badge>
                    </TableCell>
                    <TableCell>{getDepartmentName(desig.department_id)}</TableCell>
                    <TableCell>{formatDate(desig.created_at)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default DesignationsPage;
